// Spec §4.3: UI logic hook for TextInput component
import { useNavigate } from 'react-router-dom';
import type { TextInputProps } from './types';

/**
 * Empty default callback standing in when the text input receives no onChange prop. Takes no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Adapts a text input's callbacks for its parent form, defaulting a missing onChange to a no-op. Exposes onChange, which forwards the field name, the new value and props.index upward, and onClickWarningLink, which routes to the warning link with react-router navigate.
 *
 * @param props - The text input props, carrying the parent onChange callback and the field index.
 * @returns The change and warning-link click handlers.
 */
export const useTextInput = (props: TextInputProps) => {
  const { onChange: onChangeProp = NOOP } = props;
  const navigate = useNavigate();

  /**
   * Forwards a text change to the parent form together with the field position. Calls the props onChange with the field name, the new value and props.index.
   *
   * @param name - The name of the field that changed.
   * @param value - The new input value.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    onChangeProp(name, value, props.index);
  };

  /**
   * Routes to the warning link rendered beside the text input. Passes the given path to react-router navigate.
   *
   * @param warningLink - The route to navigate to.
   * @returns Nothing.
   */
  const onClickWarningLink = (warningLink: any) => {
    navigate(warningLink);
  };

  return { onChange, onClickWarningLink };
};
